import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import StatusBadge from "../../../components/ui/StatusBadge";
import Skeleton from "../../../components/ui/Skeleton";
import Modal from "../../../components/ui/Modal";
import { EmptyState } from "../../../components/ui/FeedbackStates";
import { ArrowLeft, Copy, Share2, AlertTriangle, ShieldCheck, Clock, User, Phone, Car } from "lucide-react";
import {
  cancelPreApprovedInvite,
  getPreApprovedInviteById,
} from "../../../services/inviteService";
import styles from "./InviteDetails.module.css";

const formatDateShort = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatTimeShort = (value) => {
  if (!value) return "-";
  if (typeof value === "string") {
    const match = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (match) return `${match[1].padStart(2, "0")}:${match[2]}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const InviteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [guests, setGuests] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPreApprovedInviteById(id);
      setInvite(res.invite);
      setGuests(res.guests || []);
    } catch (error) {
      console.error("INVITE DETAILS ERROR:", error);
      toast.error(error?.data?.message || error?.message || "Unable to load invite");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancelConfirm = async () => {
    setCancelling(true);
    try {
      await cancelPreApprovedInvite(id);
      toast.success("Invite cancelled successfully");
      setCancelModalOpen(false);
      await load();
    } catch (error) {
      console.error("CANCEL ERROR:", error);
      toast.error(error?.data?.message || error?.message || "Unable to cancel");
    } finally {
      setCancelling(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const sharePass = () => {
    if (!invite) return;
    const text = `AsmitA Security Gate Pass:\nPass Code: ${invite.pass_code}\nVisitor: ${invite.visitor_name || invite.title}\nValid: ${formatDateShort(invite.valid_from)} to ${formatDateShort(invite.valid_to)}`;
    if (navigator.share) {
      navigator.share({ title: "AsmitA Gate Pass", text }).catch(() => {});
    } else {
      copyToClipboard(text, "Pass details");
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Invite Details">
        <div style={{ padding: "24px" }}>
          <Skeleton variant="card" count={2} />
        </div>
      </AdminLayout>
    );
  }

  if (!invite) {
    return (
      <AdminLayout title="Invite Details">
        <EmptyState
          title="Invite not found"
          description="The requested pre-approved gate pass does not exist or has been removed."
          action={
            <Button variant="outline" onClick={() => navigate("/resident/invites")}>
              <ArrowLeft size={15} style={{ marginRight: "6px" }} /> Back to My Invites
            </Button>
          }
        />
      </AdminLayout>
    );
  }

  const isActive = String(invite.status || "").toLowerCase() === "active";

  return (
    <AdminLayout title="Invite Details">
      <div className={styles.pageGrid}>
        <div className={styles.card}>
          <div className={styles.headerRow}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "8px" }}>
                <span className={styles.titleLine}>{invite.title || "Pre-Approved Invite"}</span>
                <span className={styles.idMuted}>#{invite.id}</span>
                <StatusBadge status={invite.status} />
              </div>

              <div style={{ display: "flex", gap: "8px", alignItems: "center", color: "var(--color-text-muted)", fontSize: "13px" }}>
                <ShieldCheck size={16} color="var(--color-primary)" />
                <span style={{ fontWeight: 600 }}>{invite.invite_type?.toUpperCase()}</span>
                {invite.invite_sub_type && <span>• {invite.invite_sub_type}</span>}
              </div>
            </div>

            <div className={styles.actions}>
              <Button variant="outline" size="sm" onClick={() => navigate("/resident/invites")}>
                <ArrowLeft size={15} style={{ marginRight: "4px" }} /> Back
              </Button>
              <Button variant="outline" size="sm" onClick={sharePass}>
                <Share2 size={15} style={{ marginRight: "4px" }} /> Share
              </Button>
              {isActive ? (
                <Button variant="danger" size="sm" onClick={() => setCancelModalOpen(true)}>
                  Cancel Pass
                </Button>
              ) : null}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginTop: "20px", padding: "16px", background: "var(--color-surface-2)", borderRadius: "12px" }}>
            <div>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Pass Code</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                <span style={{ fontSize: "20px", fontWeight: 800, color: "var(--color-primary)", letterSpacing: "1px" }}>
                  {invite.pass_code || "-"}
                </span>
                {invite.pass_code && (
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(invite.pass_code, "Pass code")}>
                    <Copy size={13} />
                  </Button>
                )}
              </div>
            </div>

            <div>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Visitor / Company</div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", fontWeight: 600 }}>
                <User size={15} color="var(--color-text-muted)" />
                <span>{invite.visitor_name || invite.company_name || "-"}</span>
              </div>
              {invite.mobile_number && (
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "4px", fontSize: "13px", color: "var(--color-text-muted)" }}>
                  <Phone size={13} />
                  <span>{invite.mobile_number}</span>
                </div>
              )}
            </div>

            <div>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Validity Window</div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", fontSize: "13px" }}>
                <Clock size={15} color="var(--color-text-muted)" />
                <span>{formatDateShort(invite.valid_from)} → {formatDateShort(invite.valid_to)}</span>
              </div>
              <div style={{ fontSize: "12px", color: "var(--color-text-muted)", marginTop: "4px", paddingLeft: "21px" }}>
                {formatTimeShort(invite.start_time)} → {formatTimeShort(invite.end_time)}
              </div>
            </div>

            {invite.vehicle_number ? (
              <div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Vehicle Plate</div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", fontWeight: 600, color: "#d97706" }}>
                  <Car size={16} />
                  <span>{invite.vehicle_number}</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {guests.length > 0 ? (
          <div className={styles.card}>
            <div className={styles.titleLine} style={{ marginBottom: "12px" }}>
              Party & Group Guest Passes ({guests.length})
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead className={styles.thead}>
                  <tr>
                    <th className={styles.th}>Guest Name</th>
                    <th className={styles.th}>Mobile</th>
                    <th className={styles.th}>Vehicle</th>
                    <th className={styles.th}>Individual Pass</th>
                    <th className={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {guests.map((g) => (
                    <tr key={g.id} className={styles.tbodyRow}>
                      <td className={styles.td}>
                        <strong>{g.guest_name || "-"}</strong>
                      </td>
                      <td className={styles.td}>{g.mobile_number || "-"}</td>
                      <td className={styles.td}>{g.vehicle_number || "-"}</td>
                      <td className={[styles.td, styles.mono].join(" ")}>
                        <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>
                          {g.pass_code || "-"}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <StatusBadge status={g.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>

      {cancelModalOpen && (
        <Modal
          isOpen={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          title="Revoke Gate Pass"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "8px 0" }}>
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
              <AlertTriangle size={24} color="#ef4444" style={{ flexShrink: 0, marginTop: "2px" }} />
              <div>
                <div style={{ fontWeight: 600, color: "var(--color-text)" }}>
                  Revoke gate pass #{invite.id} immediately?
                </div>
                <div style={{ fontSize: "13px", color: "var(--color-text-muted)", marginTop: "4px" }}>
                  Pass Code: <strong>{invite.pass_code}</strong> will be invalidated. Security guards will decline entry at all gate terminals.
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
              <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
                Keep Active
              </Button>
              <Button variant="danger" disabled={cancelling} onClick={handleCancelConfirm}>
                {cancelling ? "Revoking..." : "Revoke Pass"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
};

export default InviteDetails;
